/**
 * Report Apps — TypeScript types for the report-app framework (the 5th TMS app
 * type, after Dashboard / TSM / REF / Filter).
 *
 * The report **filter pane** reuses the filter-app descriptor wire shape
 * (panes / fields / toolbar / profile) projected server-side by
 * {@code FilterDescriptorAssembler}. The one difference: reports have NO
 * {@code mode} block — it is replaced by a {@code format} block
 * ({@code {current, available:[screen, excel]}}) emitted by
 * {@code FilterDescriptorAssembler.projectFormat}. See
 * {@code documentation/implementation/system/tms-report-app.md} (server RP-1).
 *
 * The report **result** ({@code ?action=runReport}) is a generic
 * {@code {header, columns, rows}} contract projected by
 * {@code WebReportData.getReportResultJson} — rendered by the generic
 * {@link ReportTableRenderer}.
 *
 * Jackson Rule #1: every variable typed. Rule #7: module-level constants in
 * SCREAMING_SNAKE_CASE.
 */

// ── Re-exports of shared filter-app descriptor types ─────────────────────────
// Reports reuse the filter-app field / pane / toolbar / profile descriptors
// verbatim (one import place, REF/filter reuse pattern).

export type {
	ButtonDescriptor,
	FieldDescriptor,
	LookupDescriptor,
	OperatorDescriptor,
	OperatorOption,
	PaneDescriptor,
	ProfileDescriptor,
	ProfileOption,
	StatusDescriptor,
	ToolbarDescriptor,
	ToolbarGroup,
	ToolbarGroupKey,
	ActionDescriptor,
} from '../../filter-app/models/filter-app.model';

// ── Module constants (Jackson Rule #7) ───────────────────────────────────────

export const REPORT_ROUTE_PATH: string = 'report/:appName';
export const REPORT_FORMAT_SCREEN: string = 'screen';
export const REPORT_FORMAT_EXCEL: string = 'excel';

// ── Report-specific toolbar action kinds (server RP-4) ───────────────────────
// The server emits {@code kind: 'sql' | 'excel' | 'pdf'} for the report
// toolbar buttons (results_sql_button / results_excel_button /
// results_pdf_button). These are NOT in the filter-app {@code ActionDescriptor}
// union — the report page resolves them directly (it does not route report
// toolbar clicks through the shared {@code TmsToolbarActionDispatcher}, whose
// exhaustive switch would otherwise need report kinds added non-additively).

export const REPORT_ACTION_SQL: string = 'sql';
export const REPORT_ACTION_EXCEL: string = 'excel';
export const REPORT_ACTION_PDF: string = 'pdf';
// Filter-context toolbar kinds the report reuses (filter_search_button →
// 'search', filter_clear_button → 'clear-form'); handled by the report page —
// search runs the report (same path as the magnifier), clear-form resets the
// filter form to its profile defaults.
export const REPORT_ACTION_SEARCH: string = 'search';
export const REPORT_ACTION_CLEAR_FORM: string = 'clear-form';

/** The download/sql action kinds a report toolbar button carries. */
export type ReportActionKind = 'sql' | 'excel' | 'pdf';

// ── Format descriptor (replaces filter/TSM's `mode` block) ───────────────────

export interface FormatOption {
	readonly id: string;      // 'screen' | 'excel'
	readonly label: string;   // 'Screen' | 'Excel'
}

export interface FormatDescriptor {
	readonly current: string;
	readonly available: ReadonlyArray<FormatOption>;
	// Sent (false) only for a report whose layout shows no data on screen — legacy draws headings
	// only and the rows exist only in the workbook. Informational: the page no longer branches on it
	// (Screen and Excel are one flow; the format alone picks the render).
	readonly screenOutput?: boolean;
}

// ── Report app descriptor ────────────────────────────────────────────────────
// Mirrors the filter-app {@code AppDescriptor} but with a {@code format} block
// instead of {@code mode}. Imported field/pane/toolbar/profile types come from
// the filter-app model (re-exported above).

import {
	FieldDescriptor,
	PaneDescriptor,
	ProfileDescriptor,
	StatusDescriptor,
	ToolbarDescriptor,
} from '../../filter-app/models/filter-app.model';
import { WorksheetCellMeta, WorksheetSheet, WorksheetTableStructure } from '../../worksheet-app/models/worksheet-app.model';

export interface ReportAppDescriptor {
	readonly appName: string;
	readonly title: string;
	// The application's own title (its XML title), as legacy shows it; the AI page context sends it. Absent on older servers.
	readonly appTitle?: string;
	readonly format: FormatDescriptor;
	readonly formId: string;
	// Intentionally inert: the shared FilterDescriptorAssembler emits `panes` for
	// reports too (the report has a filter pane), but the report page renders a
	// single Results surface (no pane tabs) and the panel reads `fields`, not
	// `panes`. Kept for wire-shape parity with the filter-app descriptor.
	readonly panes: ReadonlyArray<PaneDescriptor>;
	readonly toolbar: ToolbarDescriptor;
	readonly profile: ProfileDescriptor;
	readonly fields: Readonly<Record<string, FieldDescriptor>>;
	readonly status: StatusDescriptor;
}

// ── Report-result contract (runReport JSON) ──────────────────────────────────
// Matches WebReportData.getReportResultJson (server RP-2). The renderer
// consumes {header, columns, rows}; `format` drives the Search download-vs-render
// branch; `referenceId` is the audit handle.

export interface ReportHeaderMetadata {
	readonly label: string;
	readonly value: string;
}

export interface ReportHeader {
	readonly logoDataUri?: string | null;
	readonly title?: string | null;
	readonly asOf?: string | null;
	readonly metadata: ReadonlyArray<ReportHeaderMetadata>;
}

/** One column's projected metadata (item / label / mask / cssRole / width / align). */
export interface ReportColumn {
	readonly item: string;
	readonly label: string;
	readonly mask?: string | null;
	// HTML class name ported from _libv__report.css (e.g. 'detailData',
	// 'reportSummary', 'overlineText', 'fieldName', 'header', 'detailTitle').
	readonly cssRole?: string | null;
	readonly widthPct?: number | null;
	readonly align?: 'l' | 'r' | 'c' | null;
	readonly productUrl?: string | null;
}

/** Typed cell value discriminator (drives sort / negative test, not render). */
export type ReportCellType = 'amount' | 'long' | 'date' | 'string';

export interface ReportCell {
	// Typed value — used for the negative (RED) test + future sort.
	readonly value: string | number | null;
	// Server-pre-masked display string (FscMask owned server-side). The
	// renderer shows THIS; it does not re-implement masks.
	readonly display: string;
	readonly type?: ReportCellType;
	readonly mask?: string | null;
	readonly cssRole?: string | null;
	// Precomputed server-side (ColumnTag RED rule). Renderer just styles it.
	readonly negative?: boolean;
	readonly align?: 'l' | 'r' | 'c' | null;
	readonly productUrl?: string | null;
}

/** Row break level — server-tagged (detail / per-level section footer / grand total). */
export type ReportRowLevel =
	| 'detail'
	| 'sectionFooter_1'
	| 'sectionFooter_2'
	| 'sectionFooter_3'
	| 'grandTotal';

/**
 * Per-page metadata the server stamps on the FIRST row of each legacy page
 * (gated JSON capture in {@code WebReportData.captureJsonRow}). {@code
 * sectionMetadata} carries THAT page's runtime SectionHeader values (Center /
 * Currency / Funding-Entity / Bank) — they vary page to page, unlike the
 * constant top-level {@link ReportHeader}.
 */
export interface ReportPageMeta {
	readonly pageNumber: number;
	readonly sectionMetadata: ReadonlyArray<ReportHeaderMetadata>;
}

export interface ReportRow {
	readonly level: ReportRowLevel;
	readonly cells: ReadonlyArray<ReportCell>;
	// Server-stamped page boundary (gated capture): true on the first row of
	// each legacy page; absent for every other row and for single-page reports.
	readonly pageBreakBefore?: boolean;
	// Present only when pageBreakBefore is true — this page's number + section
	// values. Consumed by ReportPaginationService to regroup the flat rows.
	readonly pageMeta?: ReportPageMeta;
}

/**
 * One legacy page — the client-side grouping {@code ReportPaginationService}
 * produces by splitting the flat {@code rows[]} at {@code pageBreakBefore}
 * markers. Mirrors a legacy printed page: repeated header chrome + this page's
 * {@code sectionMetadata} + its slice of rows.
 */
export interface ReportPage {
	readonly pageNumber: number;
	readonly sectionMetadata: ReadonlyArray<ReportHeaderMetadata>;
	readonly rows: ReadonlyArray<ReportRow>;
}

export interface ReportResult {
	readonly referenceId: string;
	readonly header: ReportHeader;
	readonly columns: ReadonlyArray<ReportColumn>;
	readonly rows: ReadonlyArray<ReportRow>;
	readonly coverPage?: boolean;
	readonly format?: string;
	readonly formId?: string;
	/** Legacy's finished screen HTML as a node tree (server `ReportHtmlProjector`). When present the
	 * renderer draws it, so every report matches legacy's layout; absent on older servers. */
	readonly document?: ReadonlyArray<ReportDocNode>;
	/** The main report's query returned no records (its workbook holds headings only). */
	readonly noRecords?: boolean;
	/** The run's status line ("Report: <app> successfully generated, Reference 1 ID: <id>"), as legacy shows it. */
	readonly statusMessage?: string | null;
}

/** Where the Excel view of the last run got to: shown, downloaded, Download failed, or the run itself failed. */
// 'no-records': the run found nothing (the page shows the shared empty state, plus the headings-only file).
// 'ready': a run with records — the page shows the workbook and the user downloads it.
export type ReportWorkbookPhase = 'downloaded' | 'failed' | 'run-failed' | 'no-records' | 'ready';

/** The Excel view of the last run on this report page — drives its status card / banner and toast. */
export interface ReportWorkbookStatus {
	readonly phase: ReportWorkbookPhase;
	readonly fileName: string | null;
	/** epoch ms when the file was handed over (browser clock); null until downloaded. */
	readonly at: number | null;
	/** The run behind the file found no records. */
	readonly noRecords: boolean;
	/** The server's reason when the run itself failed ('run-failed'); null otherwise. */
	readonly message: string | null;
}

/** What a status card / the workbook banner shows; also the shared empty state for both formats. */
export interface ReportWorkbookView {
	readonly tone: 'info' | 'progress' | 'success' | 'warning' | 'error';
	readonly icon: string;
	readonly headline: string;
	readonly hint: string;
	/** The main action offered: run, download, open the filters, apply the suggested range, or none while loading. */
	readonly action: ReportWorkbookAction | null;
	readonly actionLabel: string;
	/** A second, quieter action (the headings-only file after an empty run). */
	readonly secondaryAction?: ReportWorkbookAction;
	readonly secondaryActionLabel?: string;
}

export type ReportWorkbookAction = 'run' | 'filters' | 'apply-range' | 'download-empty' | 'download';

/**
 * After a run that found no records, the smallest date window (7 / 30 / 90 / 365 days ending at the branch system date)
 * in which the report's own Main SQL has records — server `?action=report-range-suggestion` (ReportPeriodProbe).
 */
export interface ReportRangeSuggestion {
	readonly fromAdaptor: string;
	readonly toAdaptor: string;
	/** The dates as the panel writes them (`dd-MMM-yy`). */
	readonly from: string;
	readonly to: string;
	/** The same dates as `yyyy-MM-dd`, for display. */
	readonly fromIso: string;
	readonly toIso: string;
	readonly days: number;
	/** Source records the report's Main SQL has in the window (not drawn rows). */
	readonly records: number;
	/** Operators written with the dates, e.g. a registered range on `=` switched to `>=`. */
	readonly operatorChanges: Readonly<Record<string, string>>;
}

export interface ReportRangeSuggestionResponse {
	readonly suggestion: ReportRangeSuggestion | null;
}

/** The empty-result card's copy for a range suggestion. */
export interface ReportRangeOffer {
	readonly headline: string;
	readonly hint: string;
	readonly actionLabel: string;
}

/** A cell's styling without its position — one entry of a preview sheet's style table. */
export type ReportWorkbookCellStyle = Omit<WorksheetCellMeta, 'rowIndex' | 'colIndex'>;

/**
 * One sheet of the run's workbook as sent for the preview: the Workbooks Import shape, with the per-cell styles sent
 * once ({@code fscCellStyles}) plus, per row, {@code [rowIndex, s0, s1, …]} — each column's 1-based style number, 0 =
 * none ({@code fscCellStyleRows}). Expanded to the Import shape by {@code expandWorkbookPreviewSheets}.
 */
export interface ReportWorkbookPreviewSheet {
	readonly name: string;
	readonly tableData: ReadonlyArray<ReadonlyArray<string | number | null>>;
	readonly tableStructure: {
		readonly initSettings: WorksheetTableStructure;
		readonly fscCellStyles: ReadonlyArray<ReportWorkbookCellStyle>;
		readonly fscCellStyleRows: ReadonlyArray<ReadonlyArray<number>>;
		/** The workbook's hyperlink cells, {@code [rowIndex, colIndex, url]}; absent when there are none. */
		readonly fscCellLinks?: ReadonlyArray<readonly [number, number, string]>;
	};
}

/** A sheet the preview shows only the first rows of. */
export interface ReportWorkbookTruncation {
	readonly sheet: string;
	readonly shownRows: number;
	readonly totalRows: number;
}

/** {@code POST ?action=report-workbook-preview} — empty {@code sheets} when the run produced no workbook. */
export interface ReportWorkbookPreviewResponse {
	readonly sheets: ReadonlyArray<ReportWorkbookPreviewSheet>;
	readonly truncated?: ReadonlyArray<ReportWorkbookTruncation>;
}

/** The workbook preview the page draws with the Workbooks grid. */
export interface ReportWorkbookPreview {
	readonly sheets: ReadonlyArray<WorksheetSheet>;
	readonly truncated: ReadonlyArray<ReportWorkbookTruncation>;
}

/** Why the report page has nothing to show: it could not open, or its run failed. */
export interface ReportFailureView {
	readonly headline: string;
	/** The server's reason. */
	readonly message: string;
}

// ── Report document — legacy screen HTML projected as typed nodes ───────────

/** Text alignment carried from the legacy cell's `text-align` / `align`. */
export type ReportDocAlign = 'left' | 'right' | 'center' | 'justify';

/** An element node. `t` is one of the fixed tag set the server keeps; `c` holds legacy CSS class names. */
export interface ReportDocElement {
	readonly t: string;
	readonly c?: string;
	readonly colspan?: number;
	readonly rowspan?: number;
	readonly width?: string;
	readonly align?: ReportDocAlign;
	/** Legacy's red text (negative amounts). */
	readonly negative?: boolean;
	/** The element's other inline style declarations (property → value), applied inline as in legacy. */
	readonly style?: Readonly<Record<string, string>>;
	readonly href?: string;
	/** ATOM transaction id of a record link: the record opens inside the app (no legacy URL is sent). */
	readonly trans?: string;
	readonly target?: string;
	/** The tenant logo: drawn from `header.logoDataUri`, which is sent once. */
	readonly logo?: boolean;
	readonly k?: ReadonlyArray<ReportDocNode>;
}

/** A text node (entities already decoded; Angular escapes on render). */
export interface ReportDocText {
	readonly x: string;
}

export type ReportDocNode = ReportDocElement | ReportDocText;

// ── Path B snapshot variant ──────────────────────────────────────────────────
// Reports are run-and-render — the result is cached server-side by formId
// (rebind re-attaches the same BAP). The thin client slice is UI-only: which
// format was picked, whether the filter panel was open, the result scroll.

export interface ReportAppSnapshot {
	readonly kind: 'report';
	readonly appName: string;
	readonly formId: string | null;
	readonly profileCd: string | null;
	readonly format: string;
	readonly filterPanelOpen: boolean;
	readonly scrollTop: number;
	/** Leading result pages drawn when the user left (later pages render on scroll); restored first so `scrollTop` lands on the same content. */
	readonly renderedPages: number;
}

/** Where the result view was when the user left the report — re-applied once the restored result renders. */
export interface ReportRestoredView {
	readonly scrollTop: number;
	readonly renderedPages: number;
}

/**
 * Does this profile's output go to a FILE rather than the screen? The one place that decides,
 * because three call sites need the same answer and they must not drift: the bootstrap (whether
 * to auto-run at all — a navigation must not download), {@code RunReport} (whether producing the
 * output means handing over a workbook), and the page body (which empty state to show). Tolerates
 * a descriptor with no format block, answering "screen" — the harmless direction: rendering an
 * unexpected report is recoverable, pushing an unexpected file is not.
 */
export function isWorkbookOutput(descriptor: ReportAppDescriptor | null | undefined): boolean {
	return descriptor?.format?.current === REPORT_FORMAT_EXCEL;
}
